import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  SnackbarApiErrorMessage,
  useDataProvider,
  useEntityPermission,
  useLocalization,
  useSnackbar,
  useUserAccess,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import { useServiceModule } from '@product-framework/components/ServiceModuleProvider';
import schema from './edit.json';
import validationSchema from './validations';
import {
  fetchSP,
  fetchContact,
  fetchAddress,
  fetchDispatchProfile,
  editSP,
  editSPAddress,
  addTravelDistance,
  editSPContact,
  getMainContact,
  changeSPStatus,
  getStatusButton,
  postProcessedAddressApiData,
  resendSPOnboardingEmail,
} from './lib';

const FORM_ID = 'ServiceProviderCompanyInfoEditForm';
const PERMISSION_SP = 'SERVICEPROVIDERPROFILE';

function ServiceProviderCompanyInfoEdit({
  serviceProviderId,
  onChange = () => undefined,
}) {
  const userAccess = useUserAccess();
  const { formatMessage, messages } = useLocalization();
  const formRef = useRef();
  const snackbar = useSnackbar();
  const { getPermission } = useEntityPermission();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, get, getApiUrl, post } = useDataProvider(provider);
  const { moduleSettings } = useServiceModule();

  const commonParams = {
    put,
    get,
    getApiUrl,
    post,
    snackbar,
    pageId: serviceProviderId,
    formatMessage,
  };

  const [statusButton, setStatusButton] = useState({
    Icon: null,
    text: null,
  });
  const [currentStatus, setCurrentStatus] = useState(null);
  const [dispatchMethod, setDispatchMethod] = useState('INACTIVE');

  const fetchDefaultValues = useCallback(async () => {
    let pageData = {};
    const responseFetchSP = await fetchSP(commonParams);
    if (responseFetchSP) {
      pageData = {
        ...pageData,
        ...responseFetchSP,
      };
      // Fetch contact and address in parallel using Promise.all
      const [
        responseFetchContact,
        responseFetchAddress,
        responseDispatchProfile,
      ] = await Promise.all([
        fetchContact(commonParams),
        fetchAddress(commonParams),
        fetchDispatchProfile(commonParams),
      ]);

      if (responseFetchContact && responseFetchContact.data.length > 0) {
        const contactData = getMainContact(responseFetchContact.data[0]);
        pageData = {
          ...pageData,
          mainContact: contactData,
        };
      }

      if (responseFetchAddress && responseFetchAddress.data.length > 0) {
        const addressData = responseFetchAddress.data[0];

        pageData = {
          ...pageData,
          ...postProcessedAddressApiData(addressData),
          fullAddressData: addressData,
        };
      }
      if (responseDispatchProfile) {
        setDispatchMethod(responseDispatchProfile?.dispatchMethod);
        pageData = {
          ...pageData,
          defaultServiceTravelDistance:
            responseDispatchProfile?.defaultServiceTravelDistance,
        };
      }
      const result = {
        data: {
          ...pageData,
          serviceProviderId,
        },
        errors: null,
      };
      setCurrentStatus(responseFetchSP?.status);
      return result;
    }
    return {
      data: null,
      errors: responseFetchSP?.errors,
    };
  }, [serviceProviderId]);

  const fetchSchema = useCallback(async () => {
    try {
      const dynamicSchema = schema.reduce((acc, field) => {
        switch (field.name) {
          case 'defaultServiceTravelDistance':
            if (
              moduleSettings?.dispatchPurchased &&
              dispatchMethod !== 'INACTIVE'
            ) {
              acc.push({
                ...field,
                required: true,
                ...(!userAccess.isSA() ||
                  (!userAccess.isAdmin() && { width: 4 })),
              });
            } else {
              acc.push({
                ...field,
                ...(!userAccess.isSA() ||
                  (!userAccess.isAdmin() && { width: 4 })),
              });
            }
            break;
          case 'status':
            if (userAccess.isSA() || userAccess.isAdmin()) {
              acc.push(field);
            }
            break;
          default:
            acc.push(field);
            break;
        }
        return acc;
      }, []);

      return { data: dynamicSchema };
    } catch (err) {
      return { data: schema, errors: [{ message: err.message }] };
    }
  }, [moduleSettings?.dispatchPurchased, dispatchMethod]);

  const fetchValidationSchema = useCallback(async () => {
    try {
      const validations = validationSchema(
        formatMessage,
        getApiUrl,
        get,
        moduleSettings?.dispatchPurchased,
        dispatchMethod,
      );
      return {
        data: validations,
      };
    } catch (err) {
      return { data: null, errors: [{ message: err.message }] };
    }
  }, [moduleSettings?.dispatchPurchased, dispatchMethod, messages]);

  const onSubmit = async data => {
    const responsePermission = await getPermission({
      name: PERMISSION_SP,
    });

    // Save address first so SP edit returns the updated timeZone derived from the new address
    const responseEditAddress = await editSPAddress(
      commonParams,
      data,
      formRef,
    );
    if (!responseEditAddress?.ok) {
      return {
        nextValues: data,
        errors: responseEditAddress?.errors || [],
      };
    }

    const [spEditResponse, responseEditContact, responseTravelDistance] =
      await Promise.all([
        editSP(commonParams, data, responsePermission),
        editSPContact(commonParams, data),
        addTravelDistance(commonParams, data),
      ]);

    if (
      spEditResponse?.ok &&
      responseEditContact?.ok &&
      responseTravelDistance?.ok
    ) {
      const addressData = responseEditAddress.data;
      const addressDetails = postProcessedAddressApiData(addressData);

      const updatedPageData = {
        ...spEditResponse.data,
        ...addressDetails,
        mainContact: responseEditContact?.data,
        defaultServiceTravelDistance:
          responseTravelDistance?.data?.defaultServiceTravelDistance,
        fullAddressData: addressData,
      };

      snackbar.showMessage(
        formatMessage({
          id: 'SERVICEPROVIDER_EDIT_SUCCESS',
          defaultMessage: 'Service Provider was saved successfully',
        }),
      );
      setCurrentStatus(spEditResponse?.data?.status);

      return {
        nextValues: {
          ...updatedPageData,
          serviceProviderId,
        },
        errors: null,
        callback: () => {
          onChange();
        },
      };
    }

    let errors = [];
    if (spEditResponse?.errors) {
      errors = [...spEditResponse.errors];
    }

    if (responseEditContact?.errors) {
      errors = [...errors, ...responseEditContact.errors];
    }

    if (responseTravelDistance?.errors) {
      errors = [...errors, ...responseTravelDistance.errors];
    }
    return {
      nextValues: data,
      errors,
    };
  };

  const changeServiceProviderStatus = async () => {
    const defaultValues = formRef.current.getDefaultValues();
    const unSavedDialog = formRef.current.getActionDialog();
    const isCurrentStatusActive = defaultValues.status === 'ACTIVE';
    const data = { status: isCurrentStatusActive ? 'INACTIVE' : 'ACTIVE' };
    const response = await changeSPStatus(commonParams, data);
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'SERVICEPROVIDER_EDIT_STATUS_SUCCESS',
          defaultMessage: `Service Provider status was updated to ${isCurrentStatusActive ? 'In-Active' : 'Active'}`,
        }),
      );
      const result = {
        ...defaultValues,
        ...response?.data,
        serviceProviderId,
      };
      setCurrentStatus(result?.status);
      return {
        nextValues: result,
        errors: null,
        callback: () => {
          onChange();
          unSavedDialog.close();
        },
      };
    }
    let errors = [];

    if (response?.errors) {
      errors = [...response.errors];
    }

    return {
      nextValues: null,
      errors,
      callback: () => {
        unSavedDialog.close();
      },
    };
  };

  const showStatusChangeConfirmationDialog = () => {
    const defaultValues = formRef.current.getDefaultValues();
    const currentStatusIsActive =
      formRef?.current?.getDefaultValues()?.status === 'ACTIVE';
    const unSavedDialog = formRef.current.getActionDialog();
    unSavedDialog.init({
      title: formatMessage({
        id: 'CHANGE_STATUS',
        defaultMessage: 'Change Status',
      }),
      message: formatMessage(
        {
          id: 'CHANGE_STATUS_MESSAGE',
          defaultMessage:
            'Do you want the set the status of {name} to {status}?',
        },
        {
          name: defaultValues.name,
          status: currentStatusIsActive ? 'In-Active' : 'Active',
        },
      ),
      width: 360,
      buttons: [
        {
          label: formatMessage({
            id: 'YES',
            defaultMessage: 'Yes',
          }),
          onClick: () => {
            formRef?.current?.onFormButtonAction(FORM_ID, () =>
              changeServiceProviderStatus(),
            );
          },
        },
        {
          label: formatMessage({
            id: 'NO',
            defaultMessage: 'No',
          }),
          onClick: () => {
            unSavedDialog.close();
          },
          variant: 'outlined',
        },
      ],
    });
    unSavedDialog.open();
  };

  const showUnSavedChangesDialog = () => {
    const unSavedDialog = formRef.current.getActionDialog();
    unSavedDialog.init({
      message: `You have unsaved changes on the form. Do you want to save the changes before proceeding?`,
      width: 360,
      size: 'small',
      buttons: [
        {
          label: 'Save and Proceed',
          onClick: () => {
            formRef?.current?.submit({
              formId: FORM_ID,
              onSuccess: () =>
                formRef?.current?.onFormButtonAction(FORM_ID, () =>
                  changeServiceProviderStatus(),
                ),
              onFailure: () => {
                unSavedDialog.close();
              },
            });
          },
        },
        {
          label: 'Discard and Proceed',
          onClick: () => {
            formRef?.current?.reset();
            formRef?.current?.onFormButtonAction(FORM_ID, () =>
              changeServiceProviderStatus(),
            );
          },
        },
        {
          label: 'Close',
          onClick: () => {
            unSavedDialog.close();
          },
          variant: 'outlined',
        },
      ],
    });
    unSavedDialog.open();
  };

  const toggleServiceProviderStatus = async () => {
    if (formRef.current?.isDirty()) {
      showUnSavedChangesDialog();
    } else {
      showStatusChangeConfirmationDialog();
    }
  };

  const resendOnboardingEmail = async () => {
    const defaultValues = formRef.current.getDefaultValues();
    const response = await resendSPOnboardingEmail(commonParams);
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'SERVICEPROVIDER_RESEND_ONBOARDING_EMAIL_SUCCESS',
          defaultMessage: `Service Provider onboarding email was resent successfully to ${defaultValues?.name}`,
        }),
      );
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
  };

  useEffect(() => {
    setStatusButton(getStatusButton(commonParams, currentStatus, IconLibrary));
  }, [currentStatus, messages]);

  return (
    <PageSection
      label={formatMessage({
        id: 'SERVICE_PROVIDER_BASIC_INFO_EDIT_HEADING',
        defaultMessage: 'Service Provider Company Information',
      })}>
      <Form
        submitWithReadOnlyFields
        formId={FORM_ID}
        ref={formRef}
        permissionEntities={[PERMISSION_SP]}
        onSubmit={onSubmit}
        fetchSchema={fetchSchema}
        fetchValidationSchema={fetchValidationSchema}
        fetchDefaultValues={fetchDefaultValues}>
        <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
          <PermissionButton
            variant="contained"
            startIcon={<IconLibrary.Save />}
            color="primary"
            permissionName="CAN_UPDATE_SP_BASIC_INFO"
            onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
            {formatMessage({
              id: 'SERVICEPROVIDER_EDIT_SAVE_BUTTON',
              defaultMessage: 'Save Service Provider',
            })}
          </PermissionButton>

          <PermissionButton
            variant="contained"
            color="primary"
            permissionName="CAN_UPDATE_SP_BASIC_INFO"
            startIcon={<IconLibrary.Reset />}
            onClick={() => formRef.current?.reset()}>
            {formatMessage({
              id: 'SERVICEPROVIDER_EDIT_RESET_BUTTON',
              defaultMessage: 'Reset',
            })}
          </PermissionButton>
          {statusButton?.Icon && (
            <PermissionButton
              variant="contained"
              color="primary"
              permissionName="CAN_UPDATE_SP_STATUS"
              startIcon={<statusButton.Icon />}
              onClick={() => toggleServiceProviderStatus()}>
              {statusButton?.text}
            </PermissionButton>
          )}
          <PermissionButton
            variant="contained"
            color="primary"
            permissionName="CAN_RESEND_SP_ONBOARDING_EMAIL"
            startIcon={<IconLibrary.ResendEmail />}
            onClick={() => resendOnboardingEmail()}>
            {formatMessage({
              id: 'SERVICE_PROVIDER_RESEND_ONBOARDING_EMAIL_BUTTON',
              defaultMessage: 'Resend Onboarding Email',
            })}
          </PermissionButton>
        </Box>
      </Form>
    </PageSection>
  );
}

export default observer(ServiceProviderCompanyInfoEdit);
